import { AfterViewInit, Component, Input, Output } from '@angular/core';
import { UntypedFormControl, UntypedFormGroup } from '@angular/forms';
import { ReplaySubject } from 'rxjs';
import { environment } from '../../../../environments/environment';
import { SelectItem, uniqBy } from '../../../shared';
import * as fromDataServices from '../../../data-services';

@Component({
    selector: 'ef-employee-payroll-item-filter',
    templateUrl: './employee-payroll-item-filter.component.html',
    styleUrls: ['./employee-payroll-item-filter.component.scss'],
    standalone: false
})
export class EmployeePayrollItemFilterComponent implements AfterViewInit {
    private _payrollItemTypes: SelectItem[] = [];

    @Input()
    set payrollItemTypes(value: SelectItem[] | null) {
        this._payrollItemTypes = value ?? [];
        this.applyUserTypeToPayrollItemTypes();
    }
    get payrollItemTypes(): SelectItem[] {
        return this._payrollItemTypes;
    }

    @Input() payrollItems: SelectItem[];

    private _employees: SelectItem[] = [];

    @Input()
    set employees(value: SelectItem[] | null) {
        this._employees = value ?? [];
        this.rebuildFilteredEmployees();
    }
    get employees(): SelectItem[] {
        return this._employees;
    }

    /** Employee dropdown options after applying User Type filter. */
    filteredEmployeeSelectItems: SelectItem[] = [];

    private _userTypes: fromDataServices.ManageUsers.LoginModel[] = [];

    @Input()
    set userTypes(value: fromDataServices.ManageUsers.LoginModel[] | null) {
        this._userTypes = value ?? [];
        this.rebuildUserTypeSelectItems();
    }
    get userTypes(): fromDataServices.ManageUsers.LoginModel[] {
        return this._userTypes;
    }
    @Output() FillGrid = new ReplaySubject<{
        employees: SelectItem[];
        payrollItems: SelectItem[];
        userTypes: SelectItem[];
    }>(1);
    @Output() FilterPayrollItemType = new ReplaySubject<string>(1);
    public dropdownItemHeight = environment.dropdownItemHeight;
    public filterFormGroup: UntypedFormGroup;
    public UserTypeSelectItems: SelectItem[] = [];
    public filteredPayrollItemTypes: SelectItem[] = [];
    constructor() {
        this.filterFormGroup = new UntypedFormGroup({
            payrollItemType: new UntypedFormControl(null),
            payrollItem: new UntypedFormControl(null),
            employee: new UntypedFormControl(null),
            userType: new UntypedFormControl(null)
        });
    }
    ngAfterViewInit(): void {
        const controls = this.filterFormGroup.controls;
        controls['employee']?.valueChanges.subscribe(() => {
            controls['payrollItem']?.setValue(null);
            controls['payrollItemType']?.setValue(null);
        });
        controls['userType']?.valueChanges.subscribe(() => {
            this.applyUserTypeToPayrollItemTypes();
            this.rebuildFilteredEmployees();
        });
        this.rebuildFilteredEmployees();
    }

    private rebuildUserTypeSelectItems(): void {
        this.UserTypeSelectItems = uniqBy(
            this._userTypes
                .filter(employee => {
                    const t = (employee?.Usertype ?? '').trim();
                    return (
                        t.length > 0 &&
                        t.toUpperCase() !== 'CON'
                    );
                })
                .map(employee => {
                    const t = employee.Usertype!.trim();
                    return { label: t, value: t };
                }),
            x => x.value
        );
        this.rebuildFilteredEmployees();
    }

    /** Limit employee options to those matching the selected user type (prefix rules match payroll item type filter). */
    private rebuildFilteredEmployees(): void {
        const all = this._employees ?? [];
        const raw = this.filterFormGroup?.get('userType')?.value;
        const selected = (raw ?? '').toString().trim().toLowerCase();
        if (!selected) {
            this.filteredEmployeeSelectItems = [...all];
            this.pruneEmployeeSelectionIfNeeded();
            return;
        }
        const employeeMatchesUserType = (employeeListId: string): boolean => {
            const user = this._userTypes.find(
                u => String(u.EmployeeListID) === String(employeeListId)
            );
            if (!user) {
                return true;
            }
            const ut = (user.Usertype ?? '').trim().toLowerCase();
            if (selected.startsWith('salary')) {
                return ut.startsWith('salary');
            }
            if (selected.startsWith('hourly')) {
                return ut.startsWith('hourly');
            }
            return ut === selected || ut.startsWith(selected);
        };
        this.filteredEmployeeSelectItems = all.filter(opt =>
            employeeMatchesUserType(String(opt.value))
        );
        this.pruneEmployeeSelectionIfNeeded();
    }

    private pruneEmployeeSelectionIfNeeded(): void {
        const ctrl = this.filterFormGroup?.get('employee');
        if (!ctrl) return;
        const allowed = new Set(
            this.filteredEmployeeSelectItems.map(x => String(x.value))
        );
        const raw = ctrl.value;
        if (raw == null || raw === '') return;
        if (Array.isArray(raw)) {
            const next = (raw as unknown[]).filter(v =>
                allowed.has(this.coerceSelectValueToKey(v))
            );
            if (next.length !== raw.length) {
                ctrl.setValue(next.length ? next : null);
            }
        } else if (!allowed.has(this.coerceSelectValueToKey(raw))) {
            ctrl.setValue(null);
        }
    }

    private applyUserTypeToPayrollItemTypes(): void {
        const selectedUserType = (
            this.filterFormGroup?.get('userType')?.value ?? ''
        )
            .toString()
            .trim()
            .toLowerCase();

        this.filteredPayrollItemTypes = (this._payrollItemTypes ?? []).filter(
            item => {
                const pit = (item?.value ?? '')
                    .toString()
                    .trim()
                    .toLowerCase();
                if (!selectedUserType) {
                    return true;
                }
                // Salary users can pick both salary and hourly payroll item types.
                if (selectedUserType.startsWith('salary')) {
                    return pit.startsWith('salary') || pit.startsWith('hourly');
                }
                // Hourly users can pick only hourly payroll item types.
                if (selectedUserType.startsWith('hourly')) {
                    return pit.startsWith('hourly');
                }
                return true;
            }
        );

        const controls = this.filterFormGroup?.controls;
        if (!controls) return;
        const current = (controls['payrollItemType']?.value ?? []) as string[];
        const allowedValues = new Set(
            this.filteredPayrollItemTypes.map(x => x.value as string)
        );
        const next = current.filter(v => allowedValues.has(v));
        if (next.length !== current.length) {
            controls['payrollItemType']?.setValue(next.length ? next : null);
        }
    }

    /** p-multiSelect values are arrays; p-select (User Type) is a single primitive or null. */
    private valuesForMap(controlName: string): string[] {
        const raw = this.filterFormGroup.get(controlName)?.value;
        if (raw == null || raw === '') {
            return [];
        }
        if (Array.isArray(raw)) {
            return (raw as unknown[]).map(v => this.coerceSelectValueToKey(v));
        }
        return [this.coerceSelectValueToKey(raw)];
    }

    private coerceSelectValueToKey(v: unknown): string {
        if (v != null && typeof v === 'object' && 'value' in (v as object)) {
            return String((v as SelectItem).value);
        }
        return String(v);
    }

    public onFillGrid(): void {
        const employees = this.valuesForMap('employee');
        const userTypeValues = this.valuesForMap('userType');
        const payrollItems = this.valuesForMap('payrollItem');
        const item = {
            employees: employees.map(
                e => this.employees.find(x => x.value === e) as SelectItem
            ),
            payrollItems: payrollItems.map(
                p => this.payrollItems.find(x => x.value === p) as SelectItem
            ),
            userTypes: userTypeValues.map(
                u => this.UserTypeSelectItems.find(x => x.value === u) as SelectItem
            )
        };
        this.FillGrid.next(item);
    }
    public Reset(): void {
        this.filterFormGroup.reset();
        this.rebuildFilteredEmployees();
    }
}
